import { Box, Divider, Stack } from "@mui/material";
import { TOptions } from "i18next";
import { useTranslation, Trans } from "next-i18next";
import { IconInfoOuterCircle } from "src/components/icons-mui/info-outer-circle";
import { Typography } from "src/components-mui/base/typography/typography";
import { CBDetailsAccordion } from "src/components-mui/pages/detail/certificate-box/components/details-accordion";
import { CBFooter } from "src/components-mui/pages/detail/certificate-box/components/footer";
import { CBHeader } from "src/components-mui/pages/detail/certificate-box/components/header/header";
import { InfoBox } from "src/components-mui/pages/detail/certificate-box/components/info-box";
import { CBInvestedCapital } from "src/components-mui/pages/detail/certificate-box/components/invested-capital";
import { CBPrices } from "src/components-mui/pages/detail/certificate-box/components/prices";
import { CBRatioTabBar } from "src/components-mui/pages/detail/certificate-box/components/ratio-tab-bar";
import { SpacedContainer } from "src/components-mui/pages/detail/certificate-box/components/spaced-container";
import {
    CBCertificateFee,
    CBEmission,
    CBLiquidationFigure,
    CBPerformanceFee,
    CBTradingVolume,
} from "src/components-mui/pages/detail/certificate-box/components/statistics";
import { useCertificateBoxContext } from "src/components-mui/pages/detail/certificate-box/context";
import { certificateRatio } from "src/components-mui/pages/detail/certificate-box/utils";
import { useRouterLocale } from "src/utils/router/use-router-locale";

const CERTIFICATE_COUNTRY_AVAILABILITY_TEST_ID = "certificate-country-availability";
const MIN_CERTIFICATES_FOR_MULTI_LICENSE = 2;

const useCertificateTranslationOptions = () => {
    const {
        data: { certificates },
    } = useCertificateBoxContext();
    const { country } = useRouterLocale();
    const { t } = useTranslation(["wf-detail"]);

    const countryText = t(`countries.${country.toLowerCase() as "at" | "de" | "ch"}`);
    const licensedCertificates = certificates.filter(cert => cert.isLicensed);

    const translationOptions: TOptions = {
        country: countryText,
        // create indices (used in wf-detail.json) with ratios of licensed certificates (e.g., {{0}}: "10:1", {{1}}: "100:1").
        ...licensedCertificates.reduce((acc, cert, i) => {
            acc[i] = certificateRatio(cert.exchangeRatioMultiplier);
            return acc;
        }, {} as Record<number, string>),
    };

    const areMultiLicensedCertificates = licensedCertificates.length >= MIN_CERTIFICATES_FOR_MULTI_LICENSE;

    return {
        translationOptions,
        areMultiLicensedCertificates,
    };
};

export const CertificateBoxNotLicensedSingle = () => {
    const { translationOptions, areMultiLicensedCertificates } = useCertificateTranslationOptions();

    return (
        <>
            <CBHeader />
            <SpacedContainer>
                <CBRatioTabBar />
                <CBPrices />
                <Divider />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <CBCertificateFee />
                    <CBPerformanceFee />
                    <CBInvestedCapital />
                </Box>

                <Divider />

                <CBDetailsAccordion>
                    <Stack sx={{ gap: 1 }}>
                        <CBEmission />
                        <CBLiquidationFigure />
                        <CBTradingVolume />
                    </Stack>
                    <Divider sx={{ pb: 2 }} />
                </CBDetailsAccordion>

                <InfoBox icon={<IconInfoOuterCircle sx={{ mt: 0.2, width: "18px", height: "18px" }} />}>
                    <Typography
                        data-test-id={CERTIFICATE_COUNTRY_AVAILABILITY_TEST_ID}
                        sx={{
                            fontSize: "sm",
                        }}
                    >
                        <Trans
                            i18nKey={`certificate.country-availability${areMultiLicensedCertificates ? "-multi" : ""}`}
                            ns="wf-detail"
                            values={{
                                ...translationOptions,
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </Typography>
                </InfoBox>

                <CBFooter />
            </SpacedContainer>
        </>
    );
};
